import { Page, PageContent, useModalState } from '@vtex/admin-ui'
import { Form, useFormState } from '@vtex/admin-ui-form'
import { useSearchParams } from 'next/navigation'
import React, { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Event, type CalculatorType, type RnbCalculatorPayload } from 'typings'
import {
  BudgetNoAuthorizedWrite,
  FormStateContextProvider,
  useFormValidation,
} from 'ui'
import { useAnalytics } from 'use-analytics'
import { useTrack } from 'hooks'
import { useDiscountType } from '../../hooks/useDiscountType'
import { useSavePromotion } from '../../hooks/useSavePromotion'
import type { Promotion } from '../typings'
import { useBudgetSave } from './Budget/useBudgetSave'
import { Header } from './Header'
import BuyAndWinForm from './promotions/BuyAndWinForm'
import BuyTogetherForm from './promotions/BuyTogetherForm'
import CampaignForm from './promotions/CampaignForm'
import MoreForLessForm from './promotions/MoreForLessForm'
import ProgressiveDiscountForm from './promotions/ProgressiveDiscount'
import RegularForm from './promotions/RegularForm'
import { AdminPromotionReviewModal, reviewByType } from './ReviewModal'
import { TemplateInfo } from './Template/TemplateForm/TemplateInfo'

const { vestResolver } = require('@hookform/resolvers/vest')

interface Props {
  promotion: Promotion
  originalValues?: RnbCalculatorPayload
}

const FORM_BY_TYPE: Partial<Record<CalculatorType, () => JSX.Element>> = {
  combo: BuyTogetherForm,
  buyAndWin: BuyAndWinForm,
  forThePriceOf: MoreForLessForm,
  progressive: ProgressiveDiscountForm,
  regular: RegularForm,
  campaign: CampaignForm,
}

type FormComponentProps = {
  type: CalculatorType
}

const VALID_SOURCES: readonly string[] = ['budget-notification']

const messages = {
  promotionFromTemplateInfo: {
    id: 'admin/promotions.form.promotion-from-template.info',
  },
}

const FormComponent = (props: FormComponentProps) => {
  const { type } = props

  const Component = FORM_BY_TYPE[type]

  if (Component) return <Component />

  throw new Error(`Form ${type} not supported`)
}

export function PromotionForm(props: Props) {
  const { promotion, originalValues } = props

  const { type } = promotion

  const { suite } = useFormValidation('main-form')

  const formState = useFormState<Promotion>({
    defaultValues: promotion,
    resolver: vestResolver(suite()),
    shouldFocusError: true,
    reValidateMode: 'onBlur',
  })

  const discountType = useDiscountType(formState)

  const { save } = useSavePromotion()

  const reviewModalState = useModalState()

  const { track } = useAnalytics()
  const { $t } = useIntl()

  const searchParams = useSearchParams()
  const source = searchParams.get('source')
  const templateId = searchParams.get('template')

  const budgetModalState = useModalState()
  const initialPromotion = useRef(promotion)

  // Do not submit form by hitting Enter on any form input (html's default behavior)
  const checkKeyDown: React.KeyboardEventHandler = (event) => {
    if (event.key === 'Enter') event.preventDefault()
  }

  const saveForm = (data?: Promotion) => {
    return save({
      data: data ?? formState.getValues(),
      originalValues,
      initialPromotion: initialPromotion.current,
    })
  }

  const { save: saveBudget, setBudgetError } = useBudgetSave({
    formState,
    budgetModalState,
  })

  const handleModalClose = () => {
    setBudgetError(null)
    budgetModalState.hide()
  }

  const sendToAPI = async () => {
    const form = formState.getValues()
    const result = await saveBudget()
    if (result.success) {
      const formWithBudget = {
        ...form,
        restrictions: { ...form.restrictions, budget: result.budget },
      } as Promotion
      await saveForm(formWithBudget)
    }
  }

  const handleSubmit = (data: Promotion) => {
    if (data.general.active && data.type in reviewByType) {
      reviewModalState.show()
      return
    }

    sendToAPI()
  }

  const viewEventTracked = useRef(false)

  useEffect(() => {
    const isNew = !originalValues

    !viewEventTracked.current && track(Event.PROMOTION_VIEW, { type, isNew })
    viewEventTracked.current = true
  }, [originalValues, track, type])

  useTrack({
    event: Event.NAVIGATED_TO_PROMOTION,
    enable: source !== null && VALID_SOURCES.includes(source),
    payload: {
      type: promotion.type,
      archived: Boolean(originalValues?.isArchived),
      source,
      appliedFilters: [],
    },
  })

  return (
    <FormStateContextProvider
      value={{
        form: formState,
        readOnly: false,
        originalValues,
        discountType,
      }}
    >
      <Form state={formState} onSubmit={handleSubmit} onKeyDown={checkKeyDown}>
        <Page>
          <Header />
          {templateId && (
            <TemplateInfo
              label={$t(messages.promotionFromTemplateInfo)}
              variant="info"
            />
          )}
          <PageContent layout="standard">
            <FormComponent type={type} />
          </PageContent>
        </Page>
        <AdminPromotionReviewModal
          state={reviewModalState}
          onCancel={reviewModalState.hide}
          onConfirm={sendToAPI}
        />
      </Form>
      <BudgetNoAuthorizedWrite
        state={budgetModalState}
        onCancel={handleModalClose}
        onConfirm={saveForm}
      />
    </FormStateContextProvider>
  )
}
